import * as React from 'react';
import { useEffect, useState } from 'react';

import { api } from '@api/reduxApi';
import { useBulkUploadActions } from '@components/BulkUploadFlow';
import { Text } from '@components/common';
import { Basic } from '@components/shared/Skeleton';
import { CaretDownSVG } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';
import { StudySelector } from 'components/shared/StudySelector';
import { useStudies } from 'hooks/useStudies';

import { CellFC } from './types';

export const Study: CellFC = ({ row, studyId }) => {
  const [updateRepoSession, { isSuccess, data, isLoading }] = api.useUpdateRepoSessionMutation();
  const [updateInsight, { isSuccess: isUpdatingInsightSuccess, data: insight }] = api.useUpdateInsightMutation();

  const { loading, studies } = useStudies();

  const predefinedStudy = studies.find((s) => s.id === studyId);

  const { setItem } = useBulkUploadActions();

  const [edit, setEdit] = useState(!predefinedStudy);

  useEffect(() => {
    if (isSuccess) {
      setItem({ id: row.original.id, session: data });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isUpdatingInsightSuccess) {
      setItem({ ...(row.original as RepoFileItem), insight });
    }
  }, [isUpdatingInsightSuccess]);

  const [study, setStudy] = useState<Study | undefined>();

  const handleSelect = (study) => {
    if (study) {
      setStudy(study);
      if (row.original.type === 'repoFile' && row.original.insight?.id) {
        updateInsight({ ...row.original.insight, project_id: study.id });
      } else {
        updateRepoSession({
          uuid: (row.original as RecordingItem | ZoomItem).session.uuid,
          project_id: study.id
        });
      }
    }
    setEdit(false);
  };

  if (isLoading || loading) {
    return (
      <Text className='h-10 w-60'>
        <Basic width='w-60' h='10' />
      </Text>
    );
  }

  if (predefinedStudy) {
    return (
      <Text h='400' className='h-10 w-60 truncate px-4 py-3'>
        {predefinedStudy.title}
      </Text>
    );
  }

  if (row.original.type === 'repoFile' && row.original.status !== 'done') {
    return (
      <Tooltip maxWidth='200' content='The PDF Insight may be attached to a Study when the upload is complete.'>
        <div className='flex w-56 cursor-text flex-row items-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 pr-3 text-gray-500 placeholder-gray-400'>
          <input
            disabled
            className='xx-combo-input focus:outline-none block flex-1 bg-gray-50 px-3 py-2 text-sm'
            placeholder='Enter name…'
          />
          <div className='flex flex-shrink-0 flex-row items-center space-x-2 text-gray-500'>
            <CaretDownSVG className='h-3 w-3' />
          </div>
        </div>
      </Tooltip>
    );
  }

  if (!edit && study) {
    return (
      <Text h='400' className='h-10 w-60 truncate px-4 py-3'>
        {study.title}
      </Text>
    );
  }

  return <StudySelector disabled={!!study} study={study} setStudy={handleSelect} inputClassName='text-sm h-10' />;
};
